import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';

import { DataProviderQueryStatus } from '../../next/CustomDash/components/DataProvider/types';
import type { RevelioPageId } from '../../next/CustomDash/components/PageConfigProvider/types';
import { useFilterContext } from '../../next/CustomDash/filters/contexts/FilterProvider';
import type { FilterSelectionsMap } from '../../next/CustomDash/filters/types/filters';
import { isScreenerV2Page } from '../../next/CustomDash/utils/routing-helpers';
import {
  type PreferenceScope,
  UserPreferenceControlledFilterIds,
  UserPreferenceKey,
  type UserPreferences,
} from './types';
import { UserPreferencesDataProvider } from './UserPreferencesDataProvider';
import {
  filtersToPreferences,
  preferenceAdapters,
} from './utils/preference-filter-adapters';
import {
  getPreferenceScope,
  updateCustomDashFilters,
} from './utils/preferences';

const isPreferenceUserSubmitted = (
  key: UserPreferenceKey,
  activeFilterMap: FilterSelectionsMap
): boolean => {
  const adapter = preferenceAdapters[key];
  if (!adapter?.relevantFilters) return false;

  // Only check filters that are UI preference controlled
  const controlledFilters = adapter.relevantFilters.filter((fid) =>
    UserPreferenceControlledFilterIds.includes(fid as unknown as string)
  );

  // If any controlled filter is user-submitted, skip this preference
  return controlledFilters.some(
    (fid) => activeFilterMap.get(fid)?.isUserSubmitted
  );
};

const EffectRunner = ({
  preferences,
  activeFilterMap,
  affectedScope,
  revelioPageId,
}: {
  status: DataProviderQueryStatus;
  preferences: UserPreferences;
  activeFilterMap: FilterSelectionsMap;
  affectedScope: PreferenceScope;
  revelioPageId?: RevelioPageId;
}) => {
  const { dispatch } = useFilterContext();
  const lastAffectedScope = useRef<PreferenceScope>();

  useEffect(() => {
    if (lastAffectedScope.current === affectedScope) {
      return;
    }

    // Filter out preferences that have been populated from deeplinks (potentially from deeplink sharer's preferences)
    const filteredPreferences = Object.entries(preferences).reduce(
      (acc, [key, value]) => {
        const prefKey = key as UserPreferenceKey;
        if (prefKey === UserPreferenceKey.Currency) {
          return acc;
        }
        // Screener v2 pages should not apply taxonomy preferences - always use default
        if (
          prefKey === UserPreferenceKey.Taxonomies &&
          isScreenerV2Page(revelioPageId)
        ) {
          return acc;
        }
        const isUserSub = isPreferenceUserSubmitted(prefKey, activeFilterMap);
        if (!isUserSub) {
          return { ...acc, [prefKey]: value };
        }
        return acc;
      },
      {} as Partial<UserPreferences>
    );

    if (Object.keys(filteredPreferences).length > 0) {
      // Only update filters if the values are actually different from current state
      const currentPreferences = filtersToPreferences({
        activeFilterMap,
        affectedScope,
      });
      const hasChanges = preferencesHaveChanges({
        filterPreferences: filteredPreferences,
        preferences: currentPreferences,
      });

      if (hasChanges) {
        updateCustomDashFilters(dispatch, filteredPreferences);
      }
      lastAffectedScope.current = affectedScope;
    }
  }, [preferences, dispatch, activeFilterMap, affectedScope, revelioPageId]);
  return null;
};

const preferencesHaveChanges = ({
  filterPreferences,
  preferences,
}: {
  filterPreferences: Partial<UserPreferences>;
  preferences: Partial<UserPreferences>;
}) => {
  const hasChanges = Object.keys(filterPreferences).some((key) => {
    const prefKey = key as UserPreferenceKey;
    if (
      prefKey === UserPreferenceKey.ContingentWorkers ||
      prefKey === UserPreferenceKey.DataAdjustments
    ) {
      if (
        filterPreferences[prefKey] === false &&
        preferences[prefKey] === false
      ) {
        return false;
      }
      return !isEqual(filterPreferences[prefKey], preferences[prefKey]);
    }
    return !isEqual(filterPreferences[prefKey], preferences[prefKey]);
  });
  return hasChanges;
};

/**
 * Syncs the initial preferences to the filters when the affected scope changes. This is
 * for the case where people share urls with people who have different preferences. In
 * those cases, we want the preferences to show up the same as the user sharing the url.
 */
export const SyncInitialPreferencesToFilters = ({
  revelioPageId,
}: {
  revelioPageId?: RevelioPageId;
}) => {
  const { filterState, activeFilterMap } = useFilterContext();
  const affectedScope = getPreferenceScope({
    pageId: revelioPageId,
    primaryFilterId: filterState.activeKey ?? 'NO_PRIMARY_FILTER',
  });

  return (
    <UserPreferencesDataProvider
      key={affectedScope}
      affectedScope={affectedScope}
    >
      {({ data, status }) => {
        if (!data || status !== DataProviderQueryStatus.Success) {
          return null;
        }
        return (
          <EffectRunner
            key={affectedScope}
            status={status}
            preferences={data.preferences}
            activeFilterMap={activeFilterMap}
            affectedScope={affectedScope}
            revelioPageId={revelioPageId}
          />
        );
      }}
    </UserPreferencesDataProvider>
  );
};
